import * as React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import red from '@material-ui/core/colors/red';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { withStyles, WithStyles, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { shareApplicant } from '../../action/application/share-applicant';
import { loadJobUsers } from '../../action/job/load-job-users';
import { getSharedEmails } from '../../action/user/shared-emails';
import { userSelector, userSharedEmailsSelector } from '../../reducers/current-user-selectors';
import { usersForJobIdSelector } from '../../reducers/job-selectors';

import { validateEmail } from '../../businesslogic/form-validators';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';

import {
  MaterialReactSelect,
  MaterialReactCreatableSelect,
} from '../../widgets/material-react-select';
import { SingleLanguageMessageComposer } from '../../widgets/message-composer/wrappers/single-language-message-composer';
import { SettingSection } from '../../widgets/setting-section';

const styles = () =>
  createStyles({
    dialogContent: {
      minHeight: '450px',
    },
    field: {
      margin: '8px',
      marginTop: '0px',
    },
    errorText: {
      color: red[400],
    },
  });

const mapStateToProps = (state: any, { jobId }: InputProps) => ({
  user: userSelector(state),
  userSharedEmails: userSharedEmailsSelector(state),
  users: usersForJobIdSelector(state, jobId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    getSharedEmails: (username: string) => dispatch(getSharedEmails(username)),
    loadUsers: () => dispatch(loadJobUsers(ownProps.jobId)),
    shareApplicant: (
      email: Array<{ email: string; name?: string }>,
      customMessage: string,
      selectedShareUserType: string,
    ) =>
      dispatch(
        shareApplicant(ownProps.jobApplicantId, email, customMessage, selectedShareUserType),
      ),
  },
});

type InputProps = {
  jobApplicantId: number;
  jobId: number;
  onClose: () => void;
  onSharedApplicant: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps & WithStyles;

type State = {
  submitting: boolean;
  selectedShareUserType: string;
  selectedUser: Array<SelectOption<string>> | null;
  customEmails: Array<SelectOption<string>>;
  customMessage: string | null;
  emailErrorMessage: string | null;
};

const formatCreateLabel = (value: string) => `Add ${value}`;
const getOptionsFromUserSharedEmails = (userSharedEmails: Array<string> | undefined) =>
  userSharedEmails ? userSharedEmails.map((email) => ({ label: email, value: email })) : [];

class ShareApplicantDialogComponent extends React.PureComponent<Props, State> {
  state: State = {
    submitting: false,
    selectedShareUserType: 'user',
    selectedUser: null,
    customEmails: [],
    customMessage: null,
    emailErrorMessage: null,
  };

  componentDidMount() {
    const { user, actions } = this.props;
    actions.loadUsers();
    if (user.username) {
      actions.getSharedEmails(user.username);
    }
  }

  shareApplicant = async () => {
    this.setState({
      submitting: true,
    });
    const { selectedShareUserType, selectedUser, customEmails, customMessage } = this.state;
    let emailsToShare = null;
    if (selectedShareUserType === 'user' && selectedUser) {
      emailsToShare = selectedUser.map((item) => ({
        email: item.value,
        name: item.label,
      }));
    }
    if (selectedShareUserType === 'other' && customEmails) {
      emailsToShare = customEmails.map((item) => ({ email: item.value }));
    }
    if (!emailsToShare || emailsToShare.length === 0) {
      this.setState({
        submitting: false,
      });
      return;
    }
    const { actions, onSharedApplicant } = this.props;
    await actions.shareApplicant(emailsToShare, customMessage || '', selectedShareUserType);
    this.setState({
      submitting: false,
    });
    onSharedApplicant();
  };

  handleSelectedShareUserTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      selectedShareUserType: e.target.value,
      selectedUser: null,
    });
  };

  handleUserSelection = (selectedUser: Array<{ value: string; label: string }>) => {
    this.setState({ selectedUser });
  };

  handleNewCustomEmailAdded = (customEmails: Array<{ value: string; label: string }>) => {
    this.setState({ customEmails, emailErrorMessage: null });
  };

  showAddOption = (value: string) => {
    if (!value) {
      return false;
    }
    const emailErrorMessage = validateEmail(value);
    if (emailErrorMessage) {
      this.setState({
        emailErrorMessage,
      });
      return false;
    }
    this.setState({
      emailErrorMessage: emailErrorMessage || null,
    });
    return true;
  };

  handleCustomMessageChange = (message: string) => {
    this.setState({
      customMessage: message,
      emailErrorMessage: null,
    });
  };

  getUserOptions = () => {
    const { users } = this.props;
    const arr = users || [];
    return arr
      .filter((user: User) => Boolean(user.email))
      .map((user: User) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.email,
      }));
  };

  render() {
    const { onClose, classes, userSharedEmails } = this.props;
    const {
      selectedShareUserType,
      selectedUser,
      customEmails,
      customMessage,
      emailErrorMessage,
      submitting,
    } = this.state;

    let canShare = false;
    if (selectedShareUserType === 'user' && selectedUser) {
      canShare = selectedUser.length > 0;
    }
    if (selectedShareUserType === 'other' && customEmails) {
      canShare = customEmails.length > 0;
    }

    return (
      <UiDialog
        onClose={onClose}
        title="Share Applicant"
        size="sm"
        className={classes.dialogContent}
      >
        <UiDialog.Content>
          <Grid container>
            <SettingSection title="Send To" caption="Select who you want to send this applicant to">
              <Grid container item xs={12}>
                <RadioGroup
                  aria-label="status change reason"
                  name="reasonId"
                  value={selectedShareUserType}
                  onChange={this.handleSelectedShareUserTypeChange}
                  row
                >
                  <FormControlLabel value="user" control={<Radio />} label="Existing User" />
                  <FormControlLabel value="other" control={<Radio />} label="Other" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} className={classes.field}>
                {emailErrorMessage ? (
                  <Grid item xs={12}>
                    <Typography variant="caption" className={classes.errorText}>
                      {emailErrorMessage}
                    </Typography>
                  </Grid>
                ) : null}
                {selectedShareUserType === 'user' ? (
                  <MaterialReactSelect
                    value={selectedUser}
                    onChange={this.handleUserSelection}
                    options={this.getUserOptions()}
                    label="Users"
                    placeholder="Select Users"
                  />
                ) : null}
                {selectedShareUserType === 'other' ? (
                  <MaterialReactCreatableSelect
                    value={customEmails}
                    onChange={this.handleNewCustomEmailAdded}
                    options={getOptionsFromUserSharedEmails(userSharedEmails)}
                    label="Emails"
                    placeholder="Select previously used emails, or enter new ones"
                    formatCreateLabel={formatCreateLabel}
                    isValidNewOption={this.showAddOption}
                  />
                ) : null}
              </Grid>
            </SettingSection>
            <SettingSection
              title="Message"
              caption="Optionally add a custom message to be displayed at the top of this email"
            >
              <SingleLanguageMessageComposer
                onChange={this.handleCustomMessageChange}
                message={
                  customMessage || 'I really think that this applicant would be a great fit!'
                }
                enableEmail={false}
                enableText
              />
            </SettingSection>
          </Grid>
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton onClick={onClose} variant="dismiss" disabled={submitting}>
            Cancel
          </UiButton>
          <UiButton onClick={this.shareApplicant} loading={submitting} disabled={!canShare}>
            Share
          </UiButton>
        </UiDialog.Actions>
      </UiDialog>
    );
  }
}

export const ShareApplicantDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(ShareApplicantDialogComponent));
